import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { translateSignal, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  SidebarDensity,
  SidebarItem,
  SidebarUserProfile,
  SkIconComponent,
  SkSelectComponent,
  SkSidebarComponent,
  UserMenuItem,
} from '@mercadoni/stark-ui';

import type { UserMenuAction, UserRole } from '../../core/interfaces';
import {
  ClientContextService,
  ClusterService,
  FavoritesService,
  SidebarItemsBuilderService,
  ToolRegistryService,
} from '../../core/services';
import { LocaleService } from '../../core/services/i18n/locale.service';
import { NfErrorStateComponent } from '../../shared/components/nf-error-state/nf-error-state.component';
import { buildUserMenuItems } from './sidebar.config';

type SidebarMode = 'dark' | 'light';

@Component({
  selector: 'nf-sidebar',
  imports: [NfErrorStateComponent, SkSidebarComponent, SkIconComponent, SkSelectComponent, ReactiveFormsModule, TranslocoPipe],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NfSidebarComponent {
  private readonly favorites = inject(FavoritesService);
  private readonly destroyRef = inject(DestroyRef);
  readonly clientContext = inject(ClientContextService);
  private readonly clusterService = inject(ClusterService);
  private readonly sidebarBuilder = inject(SidebarItemsBuilderService);
  private readonly toolRegistry = inject(ToolRegistryService);
  private readonly localeService = inject(LocaleService);
  private readonly transloco = inject(TranslocoService);

  readonly collapsed = model<boolean>(false);
  readonly userRole = input<UserRole>('support');
  readonly userName = input<string>('User');
  readonly userEmail = input<string>('user@instaleap.io');
  readonly mode = input<SidebarMode>('dark');
  readonly density = input<SidebarDensity>('comfortable');

  readonly categoryClick = output<string>();
  readonly userMenuAction = output<UserMenuAction>();

  readonly badge = computed(() => {
    const c = this.clusterService.activeCluster();
    return { label: c.label, color: c.color };
  });

  readonly sidebarItems = computed<SidebarItem[]>(() =>
    this.sidebarBuilder.getItems(this.userRole(), this.favorites.allFavoriteIds()),
  );

  /**
   * True while the tools/categories/favorites listeners haven't settled yet — briefly true
   * right after login, since the Firebase custom-token exchange can still be in flight (see
   * FirebaseAuthSyncService.firebaseUser doc comment). Drives the sidebar skeleton so that
   * window reads as "loading" instead of as an empty sidebar.
   */
  readonly sidebarLoading = computed(() => !this.toolRegistry.loaded() || !this.favorites.loaded());

  /** First load failure among the listeners the sidebar is built from (tools, categories, favorites). */
  readonly sidebarError = computed(
    () =>
      this.toolRegistry.toolsError() ??
      this.toolRegistry.categoriesError() ??
      this.favorites.loadError(),
  );
  private readonly favoritesLoadErrorText = translateSignal('layout.sidebar.favoritesLoadError');
  private readonly toolsLoadErrorText = translateSignal('layout.sidebar.toolsLoadError');
  readonly sidebarErrorTitle = computed(() =>
    this.favorites.loadError() && !this.toolRegistry.toolsError() && !this.toolRegistry.categoriesError()
      ? this.favoritesLoadErrorText()
      : this.toolsLoadErrorText(),
  );

  /** Re-subscribes whichever of the sidebar's listeners failed. */
  retrySidebar(): void {
    this.toolRegistry.retry();
    if (this.favorites.loadError()) this.favorites.retry();
  }

  readonly userProfile = computed<SidebarUserProfile>(() => ({
    id: 'current-user',
    name: this.userName(),
    role: this.sidebarBuilder.getUserRoleLabel(this.userRole()),
    avatarText: this.sidebarBuilder.getInitials(this.userName()),
  }));

  readonly userMenuItems = computed<UserMenuItem[]>(() => {
    this.localeService.locale(); // labels are rebuilt when the language changes
    return buildUserMenuItems((key) => this.transloco.translate(key));
  });

  readonly selectedClient = computed(() => this.clientContext.selectedClient());

  readonly clientControl = new FormControl('', { nonNullable: true });

  constructor() {
    // Keep FormControl in sync with the service signal
    effect(() => {
      const id = this.clientContext.selectedClientId();
      if (this.clientControl.value !== id) {
        this.clientControl.setValue(id, { emitEvent: false });
      }
    });

    // Propagate user selection to the service
    this.clientControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      this.clientContext.selectClient(id);
    });
  }

  onActionClick(id: string): void {
    this.categoryClick.emit(id);
  }

  onUserProfileClick(): void {
    this.userMenuAction.emit('profile');
  }

  onUserMenuItemClick(actionId: string): void {
    this.userMenuAction.emit(actionId as UserMenuAction);
  }
}
